/** @jsxImportSource @emotion/react */
import React, { useCallback, useState } from 'react';
import { Button, Form, Input, Space, Typography } from 'antd';
import { CloseOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import type { FormListFieldData, FormListOperation } from 'antd/es/form';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import commonStyles from '../../styles/commonStyles';
import './AttributeOptionsEditor.css';

const { Text } = Typography;

export type OptionsListProps = {
  fields: FormListFieldData[];
  operations: FormListOperation;
  helperText: string;
  emptyText: string;
  listName: (string | number)[];
  onListChange?: () => void;
};

export const AttributeOptionsEditor: React.FC<OptionsListProps> = ({
  fields,
  operations,
  helperText,
  emptyText,
  listName,
  onListChange,
}) => {
  const { t } = useTranslation('crops');
  const { add, remove, move } = operations;
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const triggerListChange = useCallback(() => {
    if (!onListChange) return;
    Promise.resolve().then(onListChange);
  }, [onListChange]);

  const handleDragStart = (index: number) => (event: React.DragEvent) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
    setDraggingIndex(index);
    setOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggingIndex(null);
    setOverIndex(null);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDragEnter = (index: number) => (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (draggingIndex !== null && overIndex !== index) {
      setOverIndex(index);
    }
  };

  const handleDrop = (index: number) => (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (draggingIndex !== null && draggingIndex !== index) {
      move(draggingIndex, index);
      triggerListChange();
    }
    setDraggingIndex(null);
    setOverIndex(null);
  };

  const hasOptions = fields.length > 0;

  return (
    <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
      <Text type="secondary">{helperText}</Text>
      {!hasOptions && <Text type="secondary">{emptyText}</Text>}
      {fields.map((field, index) => {
        const { name, key, ...restField } = field;
        const isActive = draggingIndex !== null && overIndex === index;
        return (
          <div
            key={key}
            className={`attribute-option-row${isActive ? ' attribute-option-row--active' : ''}`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter(index)}
            onDrop={handleDrop(index)}
          >
            <Button
              type="text"
              icon={<HolderOutlined />}
              draggable
              onDragStart={handleDragStart(index)}
              onDragEnd={handleDragEnd}
              className="attribute-option-row__drag"
            />
            <div className="attribute-option-row__inputs">
              <Form.Item
                {...restField}
                name={[name, 'title']}
                rules={[{ required: true, message: t('attributes.options.titleRequired') }]}
                css={css({ marginBottom: 0 })}
                className="attribute-option-row__title"
              >
                <Input placeholder={t('attributes.options.titlePlaceholder')} allowClear />
              </Form.Item>
              <Form.Item
                {...restField}
                name={[name, 'code']}
                css={css({ marginBottom: 0 })}
                className="attribute-option-row__code"
              >
                <Input placeholder={t('attributes.options.codePlaceholder')} allowClear />
              </Form.Item>
              {/* Скрытое поле id убрано, так как не требуется для валидации */}
            </div>
            <Button
              type="text"
              danger
              icon={<CloseOutlined />}
              onClick={() => {
                remove(name);
                triggerListChange();
              }}
              aria-label={t('attributes.options.remove')}
              className="attribute-option-row__remove"
            />
          </div>
        );
      })}
      <Button
        type="dashed"
        onClick={() => {
          add({ title: '', code: '' });
          triggerListChange();
        }}
        icon={<PlusOutlined />}
        block
      >
        {t('attributes.options.add')}
      </Button>
    </Space>
  );
};